/**
 * @production
 *
 * Vera rule engine.
 *
 * Single entrypoint: `evaluateRule(rule, element)`. The engine is intentionally
 * boring — three states plus exceptions plus special cases. The interesting
 * work is in the extracted RuleSpec (which captures the code's nuance) and
 * in the per-element measurement step (in the runner).
 *
 * Design notes:
 *   - Honest about uncertainty. If a fact required to evaluate the predicate
 *     or check an exception is missing, we return `indeterminate` rather
 *     than guessing. This is the discipline that distinguishes Vera from
 *     prior permitting tools that flooded users with false positives.
 *   - Unit-aware. The predicate carries `unit`; observed values must arrive
 *     in the same unit. Conversion is deliberately the runner's job, not
 *     the engine's, so unit logic stays in one place.
 *   - Pure function. No I/O, no global state. Easy to unit-test and to call
 *     from a CLI runner OR (later) from a server endpoint.
 */
import type {
  EvaluatedElement,
  QuantityValue,
  RulePredicate,
  RuleResult,
  RuleSpec,
} from "./types";

/**
 * Strip a `vera:` IRI prefix to get the local fact-key the runner uses
 * when building `EvaluatedElement.facts`. We keep `vera:hasFoo` → `foo`
 * (camel-cased) so the JSON keys read naturally.
 */
export function ontologyPropertyToFactKey(iri: string): string {
  let local = iri.startsWith("vera:") ? iri.slice("vera:".length) : iri;
  if (local.startsWith("has") && local.length > 3) {
    local = local.charAt(3).toLowerCase() + local.slice(4);
  }
  return local;
}

/**
 * Evaluate one `appliesIf` block against an element's facts.
 *
 * Returns:
 *   - "match"          — every condition is satisfied
 *   - "no_match"       — at least one condition is contradicted
 *   - "indeterminate"  — no contradictions, but at least one fact required
 *                        to decide is unknown
 */
function matchAppliesIf(
  appliesIf: Record<string, unknown>,
  facts: EvaluatedElement["facts"]
): "match" | "no_match" | "indeterminate" {
  let sawIndeterminate = false;
  for (const [iri, expected] of Object.entries(appliesIf)) {
    const factKey = ontologyPropertyToFactKey(iri);
    const actual = facts[factKey];
    if (actual === undefined) {
      sawIndeterminate = true;
      continue;
    }
    if (Array.isArray(expected)) {
      if (!expected.includes(actual as string)) return "no_match";
    } else if (typeof expected === "object" && expected !== null) {
      // Reserved for future structured matchers (e.g. range). For now treat as no_match.
      return "no_match";
    } else if (actual !== expected) {
      return "no_match";
    }
  }
  return sawIndeterminate ? "indeterminate" : "match";
}

interface PredicateOutcome {
  status: "violation" | "compliant" | "indeterminate";
  observed?: QuantityValue;
  threshold: QuantityValue;
}

function evaluatePredicate(
  predicate: RulePredicate,
  facts: EvaluatedElement["facts"]
): PredicateOutcome {
  const factKey = ontologyPropertyToFactKey(predicate.property);
  const observed = facts[factKey];
  // Sanitization: only QuantityValue is comparable. Anything else => indeterminate.
  // (Per Secure Node.js Development §5: validate before use.)
  const isQty =
    observed &&
    typeof observed === "object" &&
    "value" in (observed as object) &&
    "unit" in (observed as object);
  if (!isQty) {
    return {
      status: "indeterminate",
      threshold: makeThreshold(predicate),
    };
  }
  const obs = observed as QuantityValue;
  if (obs.unit !== predicate.unit) {
    // Engine refuses to convert silently — runner is responsible for
    // delivering observations in the predicate's unit.
    return {
      status: "indeterminate",
      observed: obs,
      threshold: makeThreshold(predicate),
    };
  }
  const threshold = makeThreshold(predicate);
  let pass = true;
  switch (predicate.kind) {
    case "minimum":
      pass = obs.value >= (predicate.threshold ?? Number.NEGATIVE_INFINITY);
      break;
    case "maximum":
      pass = obs.value <= (predicate.threshold ?? Number.POSITIVE_INFINITY);
      break;
    case "equals":
      pass = obs.value === predicate.threshold;
      break;
    case "range":
      pass =
        obs.value >= (predicate.min ?? Number.NEGATIVE_INFINITY) &&
        obs.value <= (predicate.max ?? Number.POSITIVE_INFINITY);
      break;
  }
  return {
    status: pass ? "compliant" : "violation",
    observed: obs,
    threshold,
  };
}

function makeThreshold(predicate: RulePredicate): QuantityValue {
  if (predicate.kind === "range") {
    return { value: predicate.min ?? predicate.max ?? 0, unit: predicate.unit };
  }
  return { value: predicate.threshold ?? 0, unit: predicate.unit };
}

function elementMatchesScope(
  rule: RuleSpec,
  elementClass: RuleSpec["appliesTo"]["elementClass"],
  facts: EvaluatedElement["facts"]
): "match" | "no_match" | "indeterminate" {
  if (!rule.appliesTo.scope || rule.appliesTo.scope === "all") return "match";
  if (rule.appliesTo.scope === "means_of_egress") {
    // CRC §R310.x EERO rules apply to designated escape openings, not every
    // window with a sill height parameter authored in Revit.
    if (elementClass === "vera:Opening" || elementClass === "vera:Window") {
      const eero = facts.isRequiredEero;
      if (eero === undefined) return "indeterminate";
      return eero === true ? "match" : "no_match";
    }
    const v = facts.isOnRequiredMeansOfEgress;
    if (v === undefined) return "indeterminate";
    return v === true ? "match" : "no_match";
  }
  return "match";
}

export function evaluateRule(
  rule: RuleSpec,
  element: EvaluatedElement
): RuleResult | null {
  // Type filter: rule applies to a specific ontology class.
  if (element.ontologyType !== rule.appliesTo.elementClass) return null;

  const cite = {
    code: rule.source.code,
    section: rule.source.section,
    jurisdictionId: rule.source.jurisdictionId,
    jurisdictionDisplay: rule.source.jurisdictionDisplay,
    sourceFile: rule.source.sourceFile,
    canonicalUrl: rule.source.canonicalUrl,
    verbatim: rule.source.verbatim,
    thresholdSpan: rule.source.thresholdSpan,
  };

  const exceptionsConsidered: RuleResult["exceptionsConsidered"] = [];

  // === Special cases (tighten / redirect) ==================================
  let activePredicate = rule.predicate;
  let specialCaseMatched: string | undefined;
  for (const sc of rule.specialCases ?? []) {
    const m = matchAppliesIf(sc.appliesIf, element.facts);
    if (m === "match") {
      activePredicate = sc.predicate;
      specialCaseMatched = sc.id;
      break;
    }
  }

  // === Exceptions (waive) ==================================================
  for (const ex of rule.exceptions ?? []) {
    const m = matchAppliesIf(ex.appliesIf, element.facts);
    if (m === "match") {
      exceptionsConsidered.push({
        exceptionId: ex.id,
        decision: ex.substitutePredicate
          ? "matched_substitute"
          : "matched_waive",
      });
      if (ex.substitutePredicate) {
        activePredicate = ex.substitutePredicate;
      } else {
        return {
          ruleId: rule.ruleId,
          dbId: element.dbId,
          status: "compliant_via_exception",
          summary: `Compliant via exception: ${ex.summary}`,
          rationale: `${rule.source.code} §${rule.source.section} — exception "${ex.id}" applies. ${ex.summary}`,
          exceptionsConsidered,
          cite,
        };
      }
    } else if (m === "indeterminate") {
      exceptionsConsidered.push({
        exceptionId: ex.id,
        decision: "indeterminate",
      });
    } else {
      exceptionsConsidered.push({
        exceptionId: ex.id,
        decision: "did_not_match",
      });
    }
  }

  // === Scope check ========================================================
  const scopeMatch = elementMatchesScope(
    rule,
    element.ontologyType,
    element.facts
  );
  if (scopeMatch === "no_match") return null;

  // === Predicate evaluation ===============================================
  const predicates = [activePredicate, ...(rule.conjunctivePredicates ?? [])];
  let worstOutcome: PredicateOutcome | null = null;
  let worstPredicate = activePredicate;
  for (const pred of predicates) {
    const outcome = evaluatePredicate(pred, element.facts);
    if (outcome.status === "violation") {
      worstOutcome = outcome;
      worstPredicate = pred;
      break;
    }
    if (
      outcome.status === "indeterminate" &&
      worstOutcome?.status !== "violation"
    ) {
      worstOutcome = outcome;
      worstPredicate = pred;
    }
    if (outcome.status === "compliant" && !worstOutcome) {
      worstOutcome = outcome;
      worstPredicate = pred;
    }
  }
  const outcome =
    worstOutcome ?? evaluatePredicate(activePredicate, element.facts);
  const reportingPredicate = worstPredicate;

  // Ambient indeterminacy: if the scope is unknown OR any exception is
  // indeterminate, we cannot fully clear an element even if its measured
  // value passes the threshold (a waiver-by-exception might still apply).
  const ambientIndeterminate =
    scopeMatch === "indeterminate" ||
    exceptionsConsidered.some(e => e.decision === "indeterminate");

  if (outcome.status === "indeterminate") {
    return {
      ruleId: rule.ruleId,
      dbId: element.dbId,
      status: "indeterminate",
      thresholdApplied: outcome.threshold,
      observedValue: outcome.observed,
      summary: `Cannot evaluate ${rule.name} — required measurement is missing.`,
      rationale: outcome.observed
        ? `Element has a measurement in unit "${outcome.observed.unit}" but the rule requires "${reportingPredicate.unit}". Convert before evaluating.`
        : `${rule.source.code} §${rule.source.section} requires the property "${reportingPredicate.property}" to be authored on this element. It is not present.`,
      exceptionsConsidered,
      cite,
    };
  }

  if (outcome.status === "violation") {
    return {
      ruleId: rule.ruleId,
      dbId: element.dbId,
      status: "confirmed_violation",
      thresholdApplied: outcome.threshold,
      observedValue: outcome.observed,
      summary:
        `${rule.source.code} §${rule.source.section}: measured ${formatQty(outcome.observed)} ` +
        `${reportingPredicate.kind === "minimum" ? "<" : reportingPredicate.kind === "maximum" ? ">" : "≠"} ` +
        `required ${formatQty(outcome.threshold)}.`,
      rationale: rationaleForViolation(
        rule,
        reportingPredicate,
        outcome,
        specialCaseMatched,
        exceptionsConsidered
      ),
      exceptionsConsidered,
      cite,
    };
  }

  // outcome.status === "compliant"
  if (ambientIndeterminate) {
    return {
      ruleId: rule.ruleId,
      dbId: element.dbId,
      status: "indeterminate",
      thresholdApplied: outcome.threshold,
      observedValue: outcome.observed,
      summary: `${rule.name}: measurement passes, but applicability is unknown.`,
      rationale: `Element measures ${formatQty(outcome.observed)} which would satisfy the ${activePredicate.kind} of ${formatQty(outcome.threshold)}, but the engine could not confirm scope or all exceptions because of missing facts. Forma Code Compliance flags this as indeterminate rather than clearing the element.`,
      exceptionsConsidered,
      cite,
    };
  }
  return null;
}

function formatQty(q?: QuantityValue): string {
  if (!q) return "—";
  return `${q.value} ${q.unit}`;
}

function rationaleForViolation(
  rule: RuleSpec,
  predicate: RulePredicate,
  outcome: PredicateOutcome,
  specialCaseMatched: string | undefined,
  exceptionsConsidered: RuleResult["exceptionsConsidered"]
): string {
  const parts: string[] = [];
  parts.push(
    `${rule.source.code} §${rule.source.section} (${rule.source.title}) requires ${rule.name.toLowerCase()}.`
  );
  if (specialCaseMatched) {
    parts.push(
      `Special case "${specialCaseMatched}" applies, tightening the threshold.`
    );
  }
  if (outcome.observed) {
    parts.push(
      `Measured value: ${outcome.observed.value} ${outcome.observed.unit}. ` +
        `Required ${predicate.kind}: ${outcome.threshold.value} ${outcome.threshold.unit}.`
    );
  }
  const indet = exceptionsConsidered.filter(
    e => e.decision === "indeterminate"
  );
  if (indet.length > 0) {
    parts.push(
      `${indet.length} exception(s) could not be evaluated for lack of facts (${indet.map(e => e.exceptionId).join(", ")}). If those facts become known the result may change.`
    );
  }
  return parts.join(" ");
}
